/**
 * Object → animation authoring list (EAA-1642).
 *
 * Split out of Content3DEdit because it owns real state of its own (search,
 * which groups are expanded) and the editor was already past the size where
 * one more concern belongs in it.
 *
 * The first version of this list was flat: one row per named node, 465 of them
 * on the reference model, every row an unlabelled `Mesh_0001`. It was unusable
 * and it grew the page past the viewport. This renders the scene HIERARCHY
 * instead — top-level objects only, each expandable — inside a bounded scroll
 * region, which is both far shorter and far easier to recognise.
 */

import * as React from 'react';
import { Input, cn } from '@ai-content-hub/content-kit';
import { ChevronRight, Box } from 'lucide-react';
import type { Model3DObjectInteraction } from '../schema';
import { OBJECT_PATH_SEP, objectPathLeaf } from '../lib/modelOutline';
import { requestPreviewHighlight } from '../lib/previewHighlight';

export interface ObjectInteractionListProps {
    /** Hierarchical object paths, e.g. `Fish` / `Fish/Fin`. */
    objects: string[];
    /** Animation clip names offered per object. */
    animations: string[];
    interactions: Model3DObjectInteraction[];
    onChange: (next: Model3DObjectInteraction[]) => void;
    disabled?: boolean;
    /** Scopes highlight requests to the preview showing THIS model. */
    assetId: string;
    /** Translator from the editor's useLocale — keeps this component copy-free. */
    t: (key: string, fallback: string) => string;
}

interface ObjectNode {
    name: string;
    path: string;
    children: ObjectNode[];
}

/** Rebuild the tree the paths were flattened from. */
function buildTree(paths: string[]): ObjectNode[] {
    const roots: ObjectNode[] = [];
    const byPath = new Map<string, ObjectNode>();

    // Paths arrive parent-before-child (extractObjectPaths emits them that way),
    // but sorting makes that independent of the emitter's traversal order.
    [...paths].sort().forEach((path) => {
        const segments = path.split(OBJECT_PATH_SEP);
        let prefix = '';
        let siblings = roots;
        segments.forEach((name) => {
            prefix = prefix ? `${prefix}${OBJECT_PATH_SEP}${name}` : name;
            let node = byPath.get(prefix);
            if (!node) {
                node = { name, path: prefix, children: [] };
                byPath.set(prefix, node);
                siblings.push(node);
            }
            siblings = node.children;
        });
    });

    return roots;
}

/** Every descendant path of `path`, so a parent row can report what it covers. */
function countDescendants(node: ObjectNode): number {
    return node.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

const SEARCH_RESULT_LIMIT = 80;

/**
 * EAA-1642: the selected-row styling is INLINE, not Tailwind classes.
 *
 * This package sits at `packages/content-3d`, which no app's Tailwind `content`
 * glob matched (they list `packages/content-types/*`), so a class this file was
 * the only user of — `bg-cyan-500/15`, `text-cyan-700` — was never generated
 * and the selected row looked identical to an unselected one. The glob is fixed
 * in apps/web-content/tailwind.config.js, but a selection this load-bearing
 * should not depend on a build-time scan noticing a directory.
 */
const SELECTED_STYLE: React.CSSProperties = {
    background: 'rgba(34, 211, 238, 0.18)',
    color: '#0e7490',
    fontWeight: 600,
    boxShadow: 'inset 3px 0 0 #06b6d4',
};

export function ObjectInteractionList({
    objects,
    animations,
    interactions,
    onChange,
    disabled = false,
    assetId,
    t,
}: ObjectInteractionListProps) {
    const [query, setQuery] = React.useState('');
    const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());
    const [highlighted, setHighlighted] = React.useState('');

    /**
     * EAA-1642: clicking a name highlights that object in the live preview.
     * Object names in a glTF are frequently meaningless on their own
     * (`Mesh_0001`, `group527`), so the only reliable way to know what you are
     * about to wire up is to see it light up in the model. Clicking the same
     * name again clears it.
     */
    const handleSelect = React.useCallback((path: string) => {
        const next = highlighted === path ? '' : path;
        setHighlighted(next);
        requestPreviewHighlight({ assetId, objectPath: next || null });
    }, [assetId, highlighted]);

    // Leaving the editor must not strand a highlight in the preview.
    React.useEffect(() => {
        return () => requestPreviewHighlight({ assetId, objectPath: null });
    }, [assetId]);

    const tree = React.useMemo(() => buildTree(objects), [objects]);

    const assigned = React.useMemo(() => {
        const map = new Map<string, string>();
        interactions.forEach(({ objectName, clipName }) => {
            if (objectName && clipName) map.set(objectName, clipName);
        });
        return map;
    }, [interactions]);

    const setClip = React.useCallback((objectPath: string, clipName: string) => {
        const rest = interactions.filter((i) => i.objectName !== objectPath);
        onChange(clipName ? [...rest, { objectName: objectPath, clipName }] : rest);
    }, [interactions, onChange]);

    const toggle = React.useCallback((path: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }, []);

    const trimmedQuery = query.trim().toLowerCase();
    const matches = React.useMemo(() => {
        if (!trimmedQuery) return [];
        return objects.filter((p) => p.toLowerCase().includes(trimmedQuery));
    }, [objects, trimmedQuery]);

    const clipSelect = (path: string) => (
        <select
            value={assigned.get(path) ?? ''}
            disabled={disabled}
            onChange={(e) => setClip(path, e.target.value)}
            aria-label={path}
            className="content-3d-edit-object-clip h-8 w-48 shrink-0 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
            <option value="">{t('interactionsNone', '— No animation —')}</option>
            {animations.map((clip) => (
                <option key={clip} value={clip}>{clip}</option>
            ))}
        </select>
    );

    const renderNode = (node: ObjectNode, depth: number): React.ReactNode => {
        const isOpen = expanded.has(node.path);
        const childCount = countDescendants(node);
        const hasChildren = node.children.length > 0;
        return (
            <React.Fragment key={node.path}>
                <div
                    className="content-3d-edit-object-row flex items-center gap-2 py-1"
                    style={{ paddingLeft: depth * 16 }}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={() => toggle(node.path)}
                            aria-expanded={isOpen}
                            aria-label={node.name}
                            className="content-3d-edit-object-toggle flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:bg-muted"
                        >
                            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-90')} />
                        </button>
                    ) : (
                        <Box className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                    )}
                    <button
                        type="button"
                        onClick={() => handleSelect(node.path)}
                        title={t('interactionsHighlight', 'Click to highlight in the preview')}
                        aria-pressed={highlighted === node.path}
                        style={highlighted === node.path ? SELECTED_STYLE : undefined}
                        className={cn(
                            'content-3d-edit-object-name min-w-0 flex-1 cursor-pointer truncate rounded-sm px-2 py-1 text-left text-sm transition-colors hover:bg-muted',
                            highlighted === node.path && 'content-3d-edit-object-name--active',
                        )}
                    >
                        {node.name}
                        {hasChildren ? (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                                {t('interactionsParts', '{{count}} parts').replace('{{count}}', String(childCount))}
                            </span>
                        ) : null}
                    </button>
                    {clipSelect(node.path)}
                </div>
                {isOpen ? node.children.map((child) => renderNode(child, depth + 1)) : null}
            </React.Fragment>
        );
    };

    if (objects.length === 0) {
        return (
            <p className="text-xs text-muted-foreground">
                {t('interactionsObjectsNone', 'No clickable objects found in this model.')}
            </p>
        );
    }

    return (
        <div className="content-3d-edit-objects space-y-2">
            <Input
                value={query}
                disabled={disabled}
                placeholder={t('interactionsSearch', 'Search objects…')}
                onChange={(e) => setQuery(e.target.value)}
                className="content-3d-edit-object-search"
            />

            {/*
                EAA-1642: the list is its own scroll region. Without the cap the
                section grew with the model and pushed the rest of the editor
                off-screen — 465 rows on the reference model.
            */}
            <div className="content-3d-edit-object-list max-h-80 overflow-y-auto rounded-sm border border-border bg-background">
                {trimmedQuery ? (
                    matches.length === 0 ? (
                        <p className="p-3 text-xs text-muted-foreground">
                            {t('interactionsEmpty', 'No objects match the search.')}
                        </p>
                    ) : (
                        <div className="p-1">
                            {matches.slice(0, SEARCH_RESULT_LIMIT).map((path) => (
                                <div key={path} className="content-3d-edit-object-row flex items-center gap-2 py-1 pl-2">
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(path)}
                                        title={t('interactionsHighlight', 'Click to highlight in the preview')}
                                        aria-pressed={highlighted === path}
                                        style={highlighted === path ? SELECTED_STYLE : undefined}
                                        className={cn(
                                            'content-3d-edit-object-name min-w-0 flex-1 cursor-pointer rounded-sm px-2 py-1 text-left transition-colors hover:bg-muted',
                                            highlighted === path && 'content-3d-edit-object-name--active',
                                        )}
                                    >
                                        <div className="truncate text-sm">{objectPathLeaf(path)}</div>
                                        {path.includes(OBJECT_PATH_SEP) ? (
                                            <div className="truncate text-xs text-muted-foreground">{path}</div>
                                        ) : null}
                                    </button>
                                    {clipSelect(path)}
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div className="p-1">{tree.map((node) => renderNode(node, 0))}</div>
                )}
            </div>

            {trimmedQuery && matches.length > SEARCH_RESULT_LIMIT ? (
                <p className="text-xs text-muted-foreground">
                    {t('interactionsShowing', 'Showing {{shown}} of {{total}} objects — refine the search to see more.')
                        .replace('{{shown}}', String(SEARCH_RESULT_LIMIT))
                        .replace('{{total}}', String(matches.length))}
                </p>
            ) : null}
        </div>
    );
}
